import {
  type PropsWithChildren,
  type FunctionComponent,
  type Dispatch,
  type SetStateAction,
} from 'react'
import { createContext, useContext, useState, useRef, useCallback, useMemo } from 'react'
import styled from '@emotion/styled'
import { AccountManagementMyAccountModal } from '@retailer-platform/domain-account-management'
import { HelpIcon } from '@instacart/ids-core'
import { ICPlatformVersion } from '../../../../utils/ic-platform/versioning/versions.constants'
import { useCurrentAccountContext } from '../../../../utils/current-account/CurrentAccountContext'
import icPlatformLogo from '../assets/ic-platform.svg'
import { spacing } from '../../../foundation/spacing'
import { type NavMenuHierarchy } from '../NavMenuHierarchy'
import SelectPartner from '../../../common/select-partner/SelectPartner'
import { useNavContext } from '../../nav/utils/NavContext'
import { useAiAgent } from '../../../../utils/contexts/ai/AiAgentContext'
import useAccessControl from '../../../../legacy/components/AccessControl/useAccessControl'
import { type AccessControlConfig as ComponentActionControlConfig } from '../../../../legacy/components/AccessControl/accessControl.utils'
import { type FeatureToggle } from '../../../../legacy/contexts/featureToggles/FeatureToggleContext'
import { useWindowSizeIsWide } from '../../../../utils/window/windows.hooks'
import { AiButtonOpenChat } from '../../../common/ai-button-open-chat/AiButtonOpenChat'
import { NavMenuTopDrawer } from './NavMenuTop/NavMenuTopDrawer'
import { NavMenuLeftDrawer } from './NavMenuLeftDrawer/NavMenuLeftDrawer'
import { MENU_BAR_Z_INDEX, navBarHeight } from './navBar.constants'
import { UserSettingsMenuRetailer } from './NavUserMenu/UserSettingsMenuRetailer'
import { UserSettingsMenuAdmin } from './NavUserMenu/UserSettingsMenuAdmin'
import { NavLink } from './NavLink'
import { NavSearchButton } from './NavSearchButton'
import { HelpMenu } from './HelpMenu/HelpMenu'
import { NotificationsButton } from './NotificationsButton'
import { NavWriteAccessIndicator } from './NavWriteAccessIndicator'
import { useNavigationAi } from './useNavigationAi'

type Props = {
  navMenuHierarchy: NavMenuHierarchy
}

type ContainerProps = {
  navBarColor: string
}

const Container = styled.div<ContainerProps>(({ navBarColor }) => ({
  width: '100vw',
  position: 'relative',
  height: navBarHeight,
  backgroundColor: navBarColor,
  display: 'flex',
  alignItems: 'center',
  zIndex: MENU_BAR_Z_INDEX,
  paddingLeft: '8px',
  flexShrink: 0,
}))

const Image = styled.img({
  marginRight: spacing.X8,
  height: '18px',
  width: '179px',
  margin: '1px 24px 0px 16px',
})

const MenuBarTopContext = createContext<{
  setShowPartnerSelector: (value: boolean) => void
  setShowManageAccount: (value: boolean) => void
}>({
  setShowPartnerSelector: () => {},
  setShowManageAccount: () => {},
})

export const useMenuBarTopContext = () => useContext(MenuBarTopContext)

const NOTIFICATIONS_DRAWER_FEATURE = 'rt_notification_center'

const NotificationsDrawerAccessControlConfig: ComponentActionControlConfig = {
  featureToggles: [NOTIFICATIONS_DRAWER_FEATURE as FeatureToggle],
  minICPlatformVersion: ICPlatformVersion.r2025_07_0,
}

export const MenuBarTop: FunctionComponent<PropsWithChildren<Props>> = ({ navMenuHierarchy }) => {
  // @ts-expect-error TS2769 — strictNullChecks. TODO: fix strict null check
  const [currentlySelectedItem, setCurrentlySelectedItem] = useState<number>(null)
  const [showPartnerSelector, setShowPartnerSelector] = useState(false)
  const [showManageAccount, setShowManageAccount] = useState(false)
  const [helpMenuOpen, setHelpMenuOpen] = useState(false)
  const [isSearchBarOpen, setIsSearchBarOpen] = useState(false)

  const hasAccess = useAccessControl()
  const hasNotificationsAccess = hasAccess(NotificationsDrawerAccessControlConfig)

  const { setMostRecentClickedRoute } = useNavContext()
  const [leftDrawerOpened, setLeftDrawerOpened] = useState(false)

  useNavigationAi(navMenuHierarchy)

  const wideScreen = useWindowSizeIsWide()

  const helpButtonRef = useRef<HTMLButtonElement>(null)

  const onClose = useCallback(() => {
    setHelpMenuOpen(false)
  }, [])

  const onChangeSearchBarIsOpen = useCallback(
    (isSearchOpen: boolean) => {
      setIsSearchBarOpen(isSearchOpen)
      if (isSearchOpen) {
        setLeftDrawerOpened(false)
        // @ts-expect-error TS2345 — strictNullChecks. TODO: fix strict null check
        setCurrentlySelectedItem(null)
      }
    },
    [setIsSearchBarOpen, setLeftDrawerOpened, setCurrentlySelectedItem]
  )

  const settingsMenu =
    navMenuHierarchy.type == 'admin' ? (
      <UserSettingsMenuAdmin
        setShowPartnerSelector={setShowPartnerSelector}
        setCurrentlySelectedItem={setCurrentlySelectedItem}
        navMenuHierarchy={navMenuHierarchy}
      />
    ) : (
      <div css={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        {hasNotificationsAccess && (
          <NotificationsButton
            navBarColor={navMenuHierarchy.navBarColor}
            navBarColorHover={navMenuHierarchy.navBarColorHover}
          />
        )}
        <div css={{ position: 'relative' }}>
          <button //using default button element instead of TDS Button due to style issues
            ref={helpButtonRef}
            css={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: navBarHeight,
              width: navBarHeight,
              backgroundColor: helpMenuOpen ? 'white' : navMenuHierarchy.navBarColor,
              '&:hover': {
                backgroundColor: helpMenuOpen ? 'white' : navMenuHierarchy.navBarColorHover,
              },
            }}
            onMouseDown={() => setHelpMenuOpen(!helpMenuOpen)}
          >
            <HelpIcon
              size={24}
              css={{
                fill: helpMenuOpen ? navMenuHierarchy.navBarColor : 'white',
              }}
            />
          </button>
          <HelpMenu isOpen={helpMenuOpen} onClose={onClose} />
        </div>
        <UserSettingsMenuRetailer
          setShowPartnerSelector={setShowPartnerSelector}
          setCurrentlySelectedItem={setCurrentlySelectedItem}
          setShowManageAccount={setShowManageAccount}
        />
      </div>
    )

  const NavContainerRef = useRef(null)
  // @ts-expect-error TS2339 — strictNullChecks. TODO: fix strict null check
  const rect = NavContainerRef?.current?.getBoundingClientRect()

  const Logo = useMemo(
    () => (
      <NavLink
        onClick={() => {
          // @ts-expect-error TS2345 — strictNullChecks. TODO: fix strict null check
          setMostRecentClickedRoute(null)
        }}
        route={navMenuHierarchy.homeRoute}
      >
        <Image
          src={icPlatformLogo}
          onClick={() => {
            // @ts-expect-error TS2345 — strictNullChecks. TODO: fix strict null check
            setCurrentlySelectedItem(null)
            setLeftDrawerOpened(false)
          }}
        />
      </NavLink>
    ),
    [navMenuHierarchy.homeRoute, setMostRecentClickedRoute]
  )

  return (
    <div css={{ zIndex: MENU_BAR_Z_INDEX, position: 'relative' }} ref={NavContainerRef}>
      <MenuBarTopContext.Provider value={{ setShowPartnerSelector, setShowManageAccount }}>
        <Container navBarColor={navMenuHierarchy.navBarColor}>
          {!wideScreen && (
            <NavMenuLeftDrawer
              navMenuHierarchy={navMenuHierarchy}
              currentlySelectedItem={currentlySelectedItem}
              setCurrentlySelectedItem={setCurrentlySelectedItem}
              leftDrawerOpened={leftDrawerOpened}
              setLeftDrawerOpened={setLeftDrawerOpened}
              navBarColorHover={navMenuHierarchy.navBarColorHover}
              verticalOffset={rect?.top || 0}
            />
          )}

          {Logo}

          {wideScreen && (
            <NavMenuTopDrawer
              navMenuHierarchy={navMenuHierarchy}
              currentlySelectedItem={currentlySelectedItem}
              setCurrentlySelectedItem={setCurrentlySelectedItem}
              verticalOffset={rect?.top || 0}
            />
          )}

          <div
            css={{
              marginLeft: 'auto',
              marginRight: '16px',
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: spacing.X4,
            }}
          >
            <NavWriteAccessIndicator />
            <NavSearchButton
              navMenuHierarchy={navMenuHierarchy}
              isSearchBarOpen={isSearchBarOpen}
              onChangeSearchBarIsOpen={onChangeSearchBarIsOpen as Dispatch<SetStateAction<boolean>>}
            />
            {settingsMenu}
          </div>
        </Container>
        {showPartnerSelector && (
          <SelectPartner
            handleClose={() => {
              setShowPartnerSelector(false)
            }}
          />
        )}
        <AccountManagementMyAccountModal
          isOpen={showManageAccount}
          onClose={() => {
            setShowManageAccount(false)
          }}
        />
      </MenuBarTopContext.Provider>
    </div>
  )
}
